import { useCallback, useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  FaBars,
  FaCheckCircle,
  FaChevronDown,
  FaPhoneAlt,
  FaPlane,
  FaTaxi,
  FaTimes,
  FaWhatsapp,
} from "react-icons/fa";
import {
  trackBookingClick,
  trackPhoneClick,
  trackWhatsAppClick,
} from "../utils/analytics";

const phoneHref = "tel:+35796613191";
const whatsappHref =
  "https://wa.me/35796613191?text=Hello,%20I%20would%20like%20to%20book%20a%20taxi.";

const airportTransferLinks = [
  ["Larnaca Airport Taxi", "/larnaca-airport-taxi", "Airport pickup and departures"],
  ["Paphos Airport Taxi", "/paphos-airport-taxi", "West Cyprus airport transfers"],
  ["Cyprus Airport Transfers", "/cyprus-airport-transfers", "Larnaca and Paphos coverage"],
];

const destinationLinks = [
  ["Taxi Limassol", "/taxi-limassol", "Local rides and airport transfers"],
  ["Limassol Hotels", "/limassol-hotels", "Airport transfers to hotels and resorts"],
  ["Limassol to Larnaca Airport", "/limassol-to-larnaca-airport-taxi", "Direct departure transfer"],
  ["Limassol to Paphos Airport", "/limassol-to-paphos-airport-taxi", "Private airport route"],
  ["Limassol to Nicosia", "/limassol-to-nicosia", "Capital city taxi"],
  ["Limassol to Ayia Napa", "/limassol-to-ayia-napa", "Beach resort transfer"],
  ["Limassol to Protaras", "/limassol-to-protaras", "Fig Tree Bay and hotels"],
  ["Limassol to Troodos", "/limassol-to-troodos", "Mountain village route"],
];

function NavCardLink({ label, to, description, onClick, desktop = false }) {
  const location = useLocation();
  const active = location.pathname === to;

  return (
    <Link
      to={to}
      onClick={onClick}
      className={
        desktop
          ? `group block rounded-xl border px-4 py-3.5 outline-none transition-all duration-200 hover:border-yellow-400/30 hover:bg-yellow-400/[0.08] focus:outline-none focus-visible:border-yellow-400/40 focus-visible:bg-yellow-400/[0.08] ${
              active
                ? "border-yellow-400/30 bg-yellow-400/[0.08] text-yellow-300"
                : "border-white/[0.08] bg-white/[0.025] text-white"
            }`
          : `group block rounded-2xl border p-4 transition-all duration-200 hover:-translate-y-0.5 hover:border-yellow-300/70 hover:bg-yellow-400/10 ${
              active
                ? "border-yellow-400/60 bg-yellow-400/10 text-yellow-300"
                : "border-white/10 bg-white/[0.035] text-white"
            }`
      }
    >
      <span className={`block font-black ${desktop ? "text-[0.8125rem]" : "text-sm"}`}>
        {label}
      </span>
      <span
        className={`mt-1 block text-xs text-neutral-400 group-hover:text-neutral-300 ${
          desktop ? "leading-4" : "leading-5"
        }`}
      >
        {description}
      </span>
    </Link>
  );
}

function DesktopDropdown({
  label,
  links,
  widthClass = "w-[26rem]",
  isOpen,
  onOpen,
  onClose,
}) {
  return (
    <div
      className="group relative"
      onMouseEnter={onOpen}
      onMouseLeave={onClose}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) {
          onClose();
        }
      }}
    >
      <button
        type="button"
        onClick={() => (isOpen ? onClose() : onOpen())}
        onFocus={onOpen}
        aria-expanded={isOpen}
        className="inline-flex items-center gap-2 rounded-2xl px-4 py-3 font-bold text-neutral-200 outline-none transition-colors hover:bg-white/[0.06] hover:text-yellow-300 focus:outline-none focus-visible:bg-white/[0.06] focus-visible:text-yellow-300"
      >
        {label}
        <FaChevronDown
          className={`text-xs text-yellow-400 transition-transform duration-200 ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </button>

      <div
        className={`absolute left-1/2 top-full z-50 mt-4 -translate-x-1/2 transition-all duration-200 ${
          isOpen
            ? "visible translate-y-0 opacity-100"
            : "invisible translate-y-3 opacity-0"
        } ${widthClass}`}
      >
        <div className="overflow-hidden rounded-[1.5rem] border border-yellow-400/20 bg-[#0b0b0b]/90 p-3 shadow-xl shadow-black/40 backdrop-blur-xl">
          <div className="mb-2.5 flex items-center justify-between rounded-xl border border-white/[0.08] bg-white/[0.035] px-4 py-3">
            <p className="text-xs font-black uppercase tracking-[0.18em] text-yellow-300">
              {label}
            </p>
            <span className="rounded-full border border-yellow-400/20 bg-yellow-400/10 px-3 py-1 text-[0.6875rem] font-black text-yellow-300">
              Taxi routes
            </span>
          </div>
          <div className="grid gap-2">
            {links.map(([linkLabel, to, description]) => (
              <NavCardLink
                key={to}
                label={linkLabel}
                to={to}
                description={description}
                onClick={onClose}
                desktop
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Navbar({ onMenuOpenChange }) {
  const [open, setOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const location = useLocation();

  const closeMenus = useCallback(() => {
    setActiveDropdown(null);
    setOpen(false);
  }, []);

  useEffect(() => {
    onMenuOpenChange?.(open);
  }, [onMenuOpenChange, open]);

  useEffect(() => {
    const previousBodyOverflow = document.body.style.overflow;
    const previousHtmlOverflow = document.documentElement.style.overflow;

    if (open) {
      document.body.style.overflow = "hidden";
      document.documentElement.style.overflow = "hidden";
    }

    return () => {
      document.body.style.overflow = previousBodyOverflow;
      document.documentElement.style.overflow = previousHtmlOverflow;
    };
  }, [open]);

  useEffect(() => {
    closeMenus();
  }, [closeMenus, location.pathname]);

  return (
    <header className="fixed inset-x-0 top-0 z-50 px-2 pt-2 text-white sm:px-4">
      <div className="mx-auto max-w-7xl overflow-visible rounded-[1.75rem] border border-yellow-400/20 bg-neutral-950/90 shadow-2xl shadow-black/45 backdrop-blur-xl">
        <div className="hidden border-b border-white/10 px-5 py-2 text-xs font-semibold text-neutral-300 md:flex md:items-center md:justify-between">
          <div className="flex items-center gap-4">
            <span className="flex items-center gap-2">
              <FaCheckCircle className="text-yellow-400" />
              24/7 Available
            </span>
            <span className="flex items-center gap-2">
              <FaCheckCircle className="text-yellow-400" />
              Fixed Prices
            </span>
            <span className="flex items-center gap-2">
              <FaPlane className="text-yellow-400" />
              Flight Monitoring
            </span>
          </div>

          <div className="flex items-center gap-3">
            <a
              href={phoneHref}
              onClick={() => trackPhoneClick("navbar_desktop")}
              className="inline-flex items-center gap-2 rounded-full px-3 py-2 font-bold text-neutral-100 transition hover:bg-white/[0.06] hover:text-yellow-300"
            >
              <FaPhoneAlt className="text-yellow-400" />
              +357 96613191
            </a>
            <a
              href={whatsappHref}
              onClick={() => trackWhatsAppClick("navbar_desktop")}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 rounded-full bg-green-500 px-4 py-2 font-black text-white shadow-lg shadow-green-500/20 transition hover:-translate-y-0.5 hover:bg-green-600"
            >
              <FaWhatsapp />
              WhatsApp Booking
            </a>
          </div>
        </div>

        <nav className="relative px-4 py-4 md:px-5">
          <div className="flex items-center justify-between gap-4">
            <Link
              to="/"
              onClick={closeMenus}
              className="flex min-w-0 items-center gap-3"
            >
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-yellow-400 text-black shadow-lg shadow-yellow-400/20">
                <FaTaxi />
              </span>
              <span className="min-w-0 text-lg font-black leading-tight tracking-tight text-white sm:text-xl md:text-2xl">
                Limassol <span className="text-yellow-400">Airport Taxi</span>
              </span>
            </Link>

            <div className="hidden items-center gap-1 rounded-[1.4rem] border border-white/10 bg-white/[0.035] p-1 text-sm lg:flex">
              <Link
                to="/"
                onClick={closeMenus}
                className="rounded-2xl px-4 py-3 font-bold text-neutral-200 transition hover:bg-white/[0.06] hover:text-yellow-300"
              >
                Home
              </Link>
              <DesktopDropdown
                label="Airport Transfers"
                links={airportTransferLinks}
                isOpen={activeDropdown === "airport-transfers"}
                onOpen={() => setActiveDropdown("airport-transfers")}
                onClose={() => setActiveDropdown(null)}
              />
              <DesktopDropdown
                label="Destinations"
                links={destinationLinks}
                widthClass="w-[30rem]"
                isOpen={activeDropdown === "destinations"}
                onOpen={() => setActiveDropdown("destinations")}
                onClose={() => setActiveDropdown(null)}
              />
              <Link
                to="/cyprus"
                onClick={closeMenus}
                className="rounded-2xl px-4 py-3 font-bold text-neutral-200 transition hover:bg-white/[0.06] hover:text-yellow-300"
              >
                Cyprus Guide
              </Link>
            </div>

            <div className="flex items-center gap-2">
              <a
                href="/#booking"
                onClick={() => {
                  trackBookingClick("navbar_desktop");
                  closeMenus();
                }}
                className="hidden rounded-2xl bg-yellow-400 px-5 py-3 text-sm font-black text-black shadow-lg shadow-yellow-400/20 transition hover:-translate-y-0.5 hover:bg-yellow-300 md:inline-flex"
              >
                Book Now
              </a>
              <button
                type="button"
                onClick={() => setOpen((current) => !current)}
                aria-expanded={open}
                aria-label={open ? "Close menu" : "Open menu"}
                className="inline-flex h-12 w-12 items-center justify-center rounded-2xl border border-yellow-400/30 bg-white/[0.04] text-white transition hover:border-yellow-300 hover:text-yellow-300 lg:hidden"
              >
                {open ? <FaTimes /> : <FaBars />}
              </button>
            </div>
          </div>

          {open && (
            <div className="fixed inset-x-0 top-0 z-[100] h-screen w-screen overflow-y-auto bg-neutral-950 px-4 pt-2 text-white lg:hidden">
              <div className="sticky top-0 z-10 -mx-4 border-b border-yellow-400/20 bg-neutral-950 px-4 pb-3 pt-2">
                <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 rounded-[1.75rem] border border-yellow-400/20 bg-neutral-950 p-4 shadow-2xl shadow-black/45">
                  <Link
                    to="/"
                    onClick={closeMenus}
                    className="flex min-w-0 items-center gap-3"
                  >
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-yellow-400 text-black shadow-lg shadow-yellow-400/20">
                      <FaTaxi />
                    </span>
                    <span className="min-w-0 text-lg font-black leading-tight tracking-tight text-white sm:text-xl">
                      Limassol <span className="text-yellow-400">Airport Taxi</span>
                    </span>
                  </Link>

                  <button
                    type="button"
                    onClick={closeMenus}
                    aria-label="Close menu"
                    className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl border border-yellow-400/30 bg-white/[0.04] text-white transition hover:border-yellow-300 hover:text-yellow-300"
                  >
                    <FaTimes />
                  </button>
                </div>
              </div>

              <div className="mx-auto max-w-7xl p-4 pb-[calc(6.5rem+env(safe-area-inset-bottom))]">
                <div className="mb-4 grid grid-cols-2 gap-3">
                  <a
                    onClick={() => {
                      trackPhoneClick("navbar_mobile");
                      closeMenus();
                    }}
                    href={phoneHref}
                    className="flex min-h-[56px] items-center justify-center gap-2 rounded-2xl bg-yellow-400 px-4 py-3 text-center text-sm font-black text-black shadow-lg shadow-yellow-400/20"
                  >
                    <FaPhoneAlt />
                    Call Now
                  </a>
                  <a
                    onClick={() => {
                      trackWhatsAppClick("navbar_mobile");
                      closeMenus();
                    }}
                    href={whatsappHref}
                    target="_blank"
                    rel="noreferrer"
                    className="flex min-h-[56px] items-center justify-center gap-2 rounded-2xl bg-green-500 px-4 py-3 text-center text-sm font-black text-white shadow-lg shadow-green-500/20"
                  >
                    <FaWhatsapp />
                    WhatsApp
                  </a>
                </div>

                <div className="grid gap-3">
                  <NavCardLink
                    label="Home"
                    to="/"
                    description="Booking form and main service overview"
                    onClick={closeMenus}
                  />
                  <NavCardLink
                    label="Cyprus Guide"
                    to="/cyprus"
                    description="Beaches, villages, cities, and attractions"
                    onClick={closeMenus}
                  />
                  <a
                    onClick={() => {
                      trackBookingClick("navbar_mobile");
                      closeMenus();
                    }}
                    href="/#booking"
                    className="block rounded-2xl border border-yellow-400/50 bg-yellow-400/10 p-4 text-sm font-black text-yellow-300 transition hover:bg-yellow-400 hover:text-black"
                  >
                    Book Now
                  </a>
                </div>

                <div className="mt-5">
                  <p className="mb-3 text-xs font-black uppercase tracking-[0.18em] text-yellow-300">
                    Airport Transfers
                  </p>
                  <div className="grid gap-3">
                    {airportTransferLinks.map(([label, to, description]) => (
                      <NavCardLink
                        key={to}
                        label={label}
                        to={to}
                        description={description}
                        onClick={closeMenus}
                      />
                    ))}
                  </div>
                </div>

                <div className="mt-5">
                  <p className="mb-3 text-xs font-black uppercase tracking-[0.18em] text-yellow-300">
                    Destinations
                  </p>
                  <div className="grid gap-3">
                    {destinationLinks.map(([label, to, description]) => (
                      <NavCardLink
                        key={to}
                        label={label}
                        to={to}
                        description={description}
                        onClick={closeMenus}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </nav>
      </div>
    </header>
  );
}
